import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ExternalLink, RotateCcw, Ban, Play, Clock } from "lucide-react";
import { providerLabel, fmtMoney, purchaseChannel } from "@/lib/payments";
import { restoreStorePurchases, storeManagementUrl } from "@/lib/iap";
import { localeFor } from "@/lib/dateLocale";
import { publicOrigin } from "@/lib/publicOrigin";

export const useVerifiedSubscription = (enabled = true) => {
  const [data, setData] = useState(null);
  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    return api.get("/payments/verification/mine").then(({ data: d }) => setData(d)).catch(() => setData({}));
  }, [enabled]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { window.addEventListener("yalola:iap_synced", reload); return () => window.removeEventListener("yalola:iap_synced", reload); }, [reload]);
  return { data, reload };
};

const STATUS_META = {
  active: { key: "verified.st_active", fb: "Actif", bg: "#D1FAE5", color: "#065F46" },
  cancelled_active_until: { key: "verified.st_cancelled", fb: "Annulé — actif jusqu'à l'échéance", bg: "#FEF3C7", color: "#92400E" },
  past_due: { key: "verified.st_past_due", fb: "Paiement en échec", bg: "#FEE2E2", color: "#991B1B" },
  expired: { key: "verified.st_expired", fb: "Expiré", bg: "#F3F4F6", color: "#374151" },
  revoked: { key: "verified.st_revoked", fb: "Retiré", bg: "#F3F4F6", color: "#374151" },
  refunded: { key: "verified.st_refunded", fb: "Remboursé", bg: "#F3F4F6", color: "#374151" },
};

// « Mon abonnement Yalola Vérifié » — status, renewal date, provider, actions per provider.
export const VerifiedSubscriptionCard = ({ data, onChanged, onSubscribe }) => {
  const { t, lang, user, config, refreshMe } = useApp();
  const [busy, setBusy] = useState(null);
  if (!data) return null;
  const sub = data.subscription;
  const live = !!data.is_verified && !!sub && ["active", "cancelled_active_until", "past_due"].includes(sub.status);
  const cur = data.currency || "EUR";
  const st = STATUS_META[sub?.status] || STATUS_META.expired;
  const end = sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString(localeFor(lang), { day: "2-digit", month: "long", year: "numeric" }) : null;
  const { channel, store } = purchaseChannel(config?.payments);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); await onChanged?.(); await refreshMe?.(); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };
  const cancel = () => run("cancel", async () => { await api.post("/payments/verification/cancel"); toast.success(t("verified.cancelled_toast", "Renouvellement automatique arrêté. Ton badge reste actif jusqu'à l'échéance.")); });
  const resume = () => run("resume", async () => { await api.post("/payments/verification/resume"); toast.success(t("verified.resumed_toast", "Renouvellement automatique réactivé ✓")); });
  const portal = () => run("portal", async () => { const { data: d } = await api.post("/payments/verification/portal", { origin_url: publicOrigin() }); window.open(d.url, "_blank", "noopener"); });
  const manageStore = () => run("store", async () => { const url = await storeManagementUrl(); if (url) window.open(url, "_blank", "noopener"); });
  const restore = () => run("restore", async () => { await restoreStorePurchases({ payments: config?.payments, userId: user?.id }); toast.success(t("verified.restored_toast", "Achats synchronisés")); });

  return (
    <div className="yalola-card p-5" data-testid="verified-subscription-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} style={{ color: "var(--yalola-accent)" }} />
        <div className="font-bold font-display text-lg">{t("verified.my_sub", "Mon abonnement Yalola Vérifié")}</div>
        {live && <span className="chip text-[10px] ml-auto" style={{ background: st.bg, color: st.color }} data-testid="verified-sub-status">{t(st.key, st.fb)}</span>}
      </div>
      {live ? (
        <>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-sub-details">
            {sub.provider === "admin"
              ? t("verified.granted_admin", "Badge attribué par Yalola")
              : sub.provider === "points"
                ? t("verified.granted_points", "Badge activé avec tes points de parrainage") // lot 705
                : `${fmtMoney(sub.price ?? data.price, sub.currency || cur, lang)} ${t("verified.per_year", "/ an")} · ${providerLabel(sub.provider)}`}
            {end && ` · ${sub.status === "cancelled_active_until" || sub.provider === "points" ? t("verified.until", "actif jusqu'au") : t("verified.renews", "renouvellement le")} ${end}`}
            {!end && sub.provider === "admin" && ` · ${t("verified.lifetime", "sans expiration")}`}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {sub.provider === "stripe" && sub.status === "cancelled_active_until" && (
              <Button size="sm" className="rounded-full pill-primary" onClick={resume} disabled={!!busy} data-testid="verified-sub-resume">
                {busy === "resume" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />} {t("verified.resume", "Reprendre le renouvellement")}
              </Button>
            )}
            {sub.provider === "stripe" && sub.status !== "cancelled_active_until" && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={cancel} disabled={!!busy} data-testid="verified-sub-cancel">
                {busy === "cancel" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Ban size={14} className="mr-1" />} {t("verified.cancel", "Arrêter le renouvellement")}
              </Button>
            )}
            {sub.provider === "stripe" && sub.stripe_customer_id && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={portal} disabled={!!busy} data-testid="verified-sub-portal">
                {busy === "portal" ? <Loader2 size={14} className="animate-spin mr-1" /> : <ExternalLink size={14} className="mr-1" />} {t("verified.manage_payment", "Gérer mon paiement")}
              </Button>
            )}
            {(sub.provider === "apple" || sub.provider === "google") && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={manageStore} disabled={!!busy} data-testid="verified-sub-store">
                <ExternalLink size={14} className="mr-1" /> {t("verified.manage_store", "Gérer dans {store}").replace("{store}", providerLabel(sub.provider))}
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-sub-none">
            {sub ? `${t(st.key, st.fb)}${end ? ` · ${end}` : ""}` : t("verified.none", "Aucun abonnement actif.")}
          </p>
          {onSubscribe && data.enabled !== false && (channel === "unavailable" ? (
            <div className="mt-3 space-y-1.5">
              <Button size="sm" className="rounded-full" disabled data-testid="verified-sub-subscribe">
                <Clock size={14} className="mr-1" /> {t("verified.store_soon", "Bientôt disponible")}
              </Button>
              <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-sub-store-soon">{t("verified.store_soon_body", "L'abonnement arrive bientôt dans l'app via {store}.").replace("{store}", store || "")}</p>
            </div>
          ) : (
            <Button size="sm" className="rounded-full pill-primary mt-3" onClick={onSubscribe} data-testid="verified-sub-subscribe">
              <ShieldCheck size={14} className="mr-1" /> {t("verified.get", "Obtenir Yalola Vérifié")} · {fmtMoney(data.price, cur, lang)}{t("verified.per_year", "/ an")}
            </Button>
          ))}
        </>
      )}
      {channel === "store" && (
        <button type="button" onClick={restore} disabled={!!busy} className="mt-3 text-xs underline inline-flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-sub-restore">
          {busy === "restore" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} {t("verified.restore", "Restaurer mes achats")}
        </button>
      )}
    </div>
  );
};
